import dayjs from "dayjs";
import { HTMLAttributes } from "react";

export const isEmptyDate: (date?: string | null) => boolean = (date) => {
  if (!date) return true;
  return date.startsWith("0001-01-01");
};

export const waitTime = (time: number = 100) => {
  return new Promise<boolean>((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, time);
  });
};

export const getStr = (id?: number) => {
  if (!id) return "0";
  return id.toString();
};

export const isImage = (ext: string): boolean =>
  ext.search(/.(jpg|jpeg|png|gif)$/i) >= 0;

export const renderDate: (
  date?: Date | null | string,
  time?: boolean
) => string = (date, time = false) => {
  if (!date) return "-";
  return isEmptyDate(date?.toString())
    ? "-"
    : dayjs(date).format(time ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD");
};

export const renderTime: (date?: Date | null | string) => string = (date) => {
  if (!date) return "-";
  if (typeof date === "string" && date.length == 5) {
    return date;
  }
  return isEmptyDate(date?.toString()) ? "-" : dayjs(date).format("HH:mm");
};

export const formatAPIDate = (date?: any): string => {
  if (date) return dayjs(date).format("YYYY-MM-DDTHH:mm:ssZ");
  return "";
};

export const renderMoney: (money?: number | null) => string = (money) => {
  if (!money) return "0";
  return new Intl.NumberFormat("en", {
    maximumFractionDigits: 2,
  }).format(money);
};

export const tableCellFixed: (width: number) => {
  width: number;
  onCell: () => HTMLAttributes<HTMLElement>;
} = (width) => ({
  width,
  onCell: () => ({ style: { maxWidth: width, minWidth: width } }),
});

export const renderFullname = (
  firstName: string,
  lastName: string,
  obj?: any
) => {
  if (obj && firstName in obj && lastName in obj) {
    return obj[lastName]?.substr(0, 1) + ". " + obj[firstName];
  }
};

const keyAlertedInFilter = ["_at", "_date"];

export const tablePagination: (
  params: Partial<{
    pageSize?: number;
    current?: number;
    [x: string]: any;
  }>,
  sort: any
) => { limit: number; page: number; sorter: any; [x: string]: any } = (
  { pageSize, current, ...rest },
  sort
) => {
  const filters: { [x: string]: any } = {};
  for (const key of Object.keys(rest)) {
    if (
      !!rest[key] &&
      !keyAlertedInFilter.some((prefix) => key.endsWith(prefix))
    ) {
      filters[key] = rest[key];
    }

    if (
      keyAlertedInFilter.some((prefix) => key.endsWith(prefix)) &&
      !!rest[key] &&
      !Array.isArray(rest[key])
    ) {
      filters[key] = dayjs(rest[key]).format("YYYY-MM-DD");
    }

    if (
      keyAlertedInFilter.some((prefix) => key.endsWith(prefix)) &&
      !!rest[key] &&
      Array.isArray(rest[key])
    ) {
      filters[key] = rest[key];
    }
  }

  return {
    limit: pageSize || 20,
    page: (current || 1) - 1,
    sorter: sort,
    ...filters,
  };
};

export const findFindByAttibute: <T>(params: {
  code: keyof T;
  val: any;
  list: T[];
}) => T | null = ({ val, code, list }) => {
  const index = list.findIndex((item: any) => item[code] === val);
  if (index === -1) {
    return null;
  }
  return { ...(list[index] as any) };
};

export const renderPassengerType: (val: string) => string = (val: string) => {
  const mapToStr: any = {
    ADT: "Том хүн",
    CHD: "Хүүхэд",
    INF: "Няраа",
  };
  return mapToStr[val] ? mapToStr[val] : "-";
};

export const renderHHMM: (val?: string | null) => string = (val) => {
  if (!val) {
    return "-";
  }

  return `${val.substr(0, 2)}:${val.substr(2, 2)}`;
};

export const extractHHMM: (val?: string | number | null) => number[] = (
  val
) => {
  if (!val) {
    return [0, 0];
  }
  if (typeof val === "string") {
    val = parseInt(val);
  }

  if (val <= 0) {
    return [0, 0];
  }

  return [Math.floor(val / 60), Math.floor(val % 60)];
};

export const renderDateDDMMYY: (date: any) => string = (date) => {
  if (!date) return "-";
  return dayjs(date, "DDMMYY").format("YYYY-MM-DD");
};

export const extractDateTimeDiffrenceInHHMM = (
  startDate: string,
  endDate: string
) => {
  const sDate = dayjs(startDate, "HHmm DDMMYY");
  const eDate = dayjs(endDate, "HHmm DDMMYY");
  const diff = eDate.diff(sDate, "minute");
  return extractHHMM(diff);
};

export const getQueryVariable = (variable: string) => {
  const query = window.location.search.substring(1);
  const vars = query.split("&");
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split("=");
    if (pair[0] === variable) {
      return pair[1];
    }
  }
  return false;
};

export const renderBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = [
    "Bytes",
    "KiB",
    "MiB",
    "GiB",
    "TiB",
    "PiB",
    "EiB",
    "ZiB",
    "YiB",
  ];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const convertMinutesToHours = (minutes?: number): string => {
  if (minutes === undefined) {
    return "0";
  }

  if (minutes < 100) return `${Math.round(minutes)} мин`;

  const hours = minutes / 60;
  const roundedHours = Math.round(hours * 100) / 100; // Rounds to two decimal places

  return `${roundedHours.toString()} цаг`;
};

export const arrAnyStartWith = (arr: string[], item: string): boolean => {
  return arr.some((i) => i.startsWith(item));
};
